<template>
    <pendo-multiselect
        v-if="variant === 'dropdown'"
        class="contrast-picker__dropdown"
        :model-value="selectedOption"
        :options="options"
        :allow-empty="false"
        :aria-label="$t('themeMenu.Contrast')"
        @update:model-value="selectOption" />
    <fieldset
        v-else
        class="contrast-picker">
        <legend class="contrast-picker__legend">
            <h3 class="contrast-picker__heading">
                {{ $t('themeMenu.Contrast') }}
            </h3>
        </legend>
        <pendo-button
            v-for="option in options"
            :key="option.id"
            class="contrast-picker__option"
            type="tertiary"
            :pendo-analytics="option.analytics"
            :aria-pressed="isSelected(option)"
            @click.prevent.stop="selectOption(option)"
            @keydown.space.prevent.stop="selectOption(option)"
            @keydown.enter.prevent.stop="selectOption(option)">
            <div class="contrast-picker__option-label">
                {{ option.label }}
            </div>
            <pendo-icon
                v-if="isSelected(option)"
                class="contrast-picker__option-icon"
                type="check"
                size="14" />
        </pendo-button>
    </fieldset>
</template>

<script>
import { PendoButton, PendoIcon, PendoMultiselect } from '@pendo/components-vue3';
import useContrast from '@/common/use/use-contrast/use-contrast';

export default {
    name: 'ContrastPicker',
    components: {
        PendoButton,
        PendoIcon,
        PendoMultiselect,
    },
    props: {
        variant: {
            type: String,
            default: 'list',
            validator: (variant) => ['list', 'dropdown'].includes(variant),
        },
    },
    emits: ['select'],
    setup () {
        const { contrastPreference, setContrast } = useContrast();

        return {
            contrastPreference,
            setContrast,
        };
    },
    computed: {
        options () {
            return [
                { id: 'system', value: 'system', label: this.$t('themeMenu.System setting') },
                { id: 'standard', value: false, label: this.$t('themeMenu.Standard contrast') },
                { id: 'high', value: true, label: this.$t('themeMenu.High contrast') },
            ].map((option) => ({ ...option, analytics: `theme-popover-contrast-${option.id}` }));
        },
        selectedOption () {
            return this.options.find((option) => this.isSelected(option));
        },
    },
    methods: {
        isSelected (option) {
            return this.contrastPreference === option.value;
        },
        selectOption (option) {
            this.setContrast(option.value);
            this.$emit('select');
        },
    },
};
</script>

<style lang="less" scoped>
.contrast-picker {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    border: 0;
    min-width: 0;

    &__legend {
        float: left;
        width: 100%;
        margin: 0;
        padding: 4px 4px 2px;
        border: 0;
    }

    &__heading {
        margin: 0;
        font-family: var(--paragraph-small-semibold-font-family);
        font-size: var(--paragraph-small-semibold-font-size);
        font-weight: var(--paragraph-small-semibold-font-weight);
        line-height: var(--paragraph-small-semibold-line-height);
        color: var(--text-body-default-base);
    }

    &__option {
        margin: 0 !important;
        width: 100%;
        padding: 6px 4px !important;
        display: flex !important;

        &:focus-visible {
            outline: 2px solid var(--focus-ring-base);
            outline-offset: -1px;
        }

        &:hover {
            text-decoration: none;
            background-color: var(--surface-default-hover);
        }

        :deep(span) {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            height: 32px;

            svg {
                height: 14px;
                width: 14px;
                padding: 5px;
                box-sizing: content-box;
            }
        }

        &-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--text-body-default-base);
        }

        &-icon {
            margin-left: auto;
            color: var(--icon-stroke-subtle);
        }
    }
}
</style>
